import { getConvertedSlideCannotBeSavedText } from 'presentationEditor/convertedSlideMessages';
import { Features, hasFeature } from '@ardoq/features';
import {
  DropdownHeader,
  DropdownOption,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { IconName } from '@ardoq/icons';
import { trackClickOnSaveAs } from 'appLayout/ardoqStudio/viewPaneHeader/tracking';
import { noop } from 'lodash';
import { ViewIds } from '@ardoq/api-types';
import { addToPresentation } from 'viewSettings/exportHandlers';
import { exportToLucidchart } from 'integrations/lucidchart/utils';
import {
  asyncNoop,
  type AddToDashboardOption,
} from 'appLayout/ardoqStudio/viewPaneHeader/viewModifiers$';
import { dispatchAction } from '@ardoq/rxbeach';
import { exportToPlantUml } from 'graphExport/plantUml/actions';
import {
  hideViewSidePanel,
  showViewSidePanel,
} from 'views/mainApp/viewSidePanel/actions';
import { ViewSidePanelContentIds } from 'views/mainApp/viewSidePanel/types';
import type { SaveAsViewpointButtonState } from 'streams/saveAsViewpointButtonState$';
import { t, type I18nLocale } from '@ardoq/i18n';
import { viewPptxExportRequested } from 'appLayout/ardoqStudio/viewPaneHeader/pptxExport/actions';
import { NOT_EXPORTABLE_VIEWS } from 'presentation/pptxExport/slidePlan';

const onSaveAsViewpointClick = (isOpen: boolean) => {
  if (isOpen) {
    dispatchAction(hideViewSidePanel());
  } else {
    dispatchAction(showViewSidePanel(ViewSidePanelContentIds.SAVE_VIEWPOINT));
    trackClickOnSaveAs('viewpoint');
  }
};

const onSaveAsBookmarkClick = (isOpen: boolean) => {
  if (isOpen) {
    dispatchAction(hideViewSidePanel());
  } else {
    dispatchAction(showViewSidePanel(ViewSidePanelContentIds.SAVE_BOOKMARK));
  }
};

type PresentationSlideDisabledDescriptionArgs = {
  isViewConformant: boolean;
  isConvertedFromWorkspaceMode: boolean;
  i18nLocale: I18nLocale;
};

const getPresentationSlideDisabledDescription = ({
  isViewConformant,
  isConvertedFromWorkspaceMode,
  i18nLocale,
}: PresentationSlideDisabledDescriptionArgs): string | undefined => {
  if (!isViewConformant) {
    return t(
      i18nLocale,
      'appContainer:saveDropdown.presentationSlideDisabledViewNotConform',
      "You can't save this view as a presentation slide. Try switching to a different view."
    );
  }
  if (isConvertedFromWorkspaceMode) {
    return getConvertedSlideCannotBeSavedText(i18nLocale);
  }
  return undefined;
};

type ToSaveDropdownOptionsArgs = {
  canSaveViewpoint: boolean;
  canAddPresentationSlide: boolean;
  isSaveAsViewpointSidePanelOpened: boolean;
  isSaveAsBookmarkSidePanelOpened: boolean;
  mainViewId: ViewIds;
  isViewpointMode: boolean;
  exportToPng: () => Promise<void>;
  /** when provided (block diagram viewpoint mode), adds an SVG export option */
  exportToSvg?: () => Promise<void>;
  /** the add-to-dashboard entry point (bubble chart viewpoint mode); null when unsupported */
  addToDashboard: AddToDashboardOption | null;
  isLegendActive: boolean;
  hasLucidchartFeature: boolean;
  hasPptxExportFeature: boolean;
  isViewConformant: boolean;
  isConvertedFromWorkspaceMode: boolean;
  saveAsViewpointButtonState: SaveAsViewpointButtonState;
  i18nLocale: I18nLocale;
};

export const toSaveDropdownOptions = ({
  canSaveViewpoint,
  canAddPresentationSlide,
  isSaveAsViewpointSidePanelOpened,
  isSaveAsBookmarkSidePanelOpened,
  mainViewId,
  isViewpointMode,
  exportToPng,
  exportToSvg,
  addToDashboard,
  isLegendActive,
  hasLucidchartFeature,
  hasPptxExportFeature,
  isViewConformant,
  isConvertedFromWorkspaceMode,
  saveAsViewpointButtonState,
  i18nLocale,
}: ToSaveDropdownOptionsArgs): Array<DropdownHeader | DropdownOption> => {
  const isSlideSavable = isViewConformant && !isConvertedFromWorkspaceMode;
  return [
    ...(canSaveViewpoint || canAddPresentationSlide
      ? [
          {
            type: DropdownOptionType.HEADER,
            label: t(
              i18nLocale,
              'appContainer:saveDropdown.saveAsHeader',
              'Save as'
            ),
          } satisfies DropdownHeader,
        ]
      : []),
    ...(canSaveViewpoint
      ? [
          {
            type: DropdownOptionType.OPTION,
            iconName: IconName.VIEWPOINT,
            label: t(
              i18nLocale,
              'appContainer:saveDropdown.viewpoint',
              'Viewpoint'
            ),
            isDisabled: saveAsViewpointButtonState.isDisabled,
            description: saveAsViewpointButtonState.disabledDescription,
            onClick: isViewConformant
              ? () => {
                  onSaveAsViewpointClick(isSaveAsViewpointSidePanelOpened);
                }
              : noop,
            isFocusedByDefault: true,
          } satisfies DropdownOption,
        ]
      : []),
    ...(canAddPresentationSlide
      ? [
          {
            type: DropdownOptionType.OPTION,
            iconName: IconName.ADD_PRESENTATION,
            label: t(
              i18nLocale,
              'appContainer:saveDropdown.presentationSlide',
              'Presentation slide'
            ),
            onClick: isSlideSavable
              ? () => {
                  addToPresentation(mainViewId, isViewpointMode);
                  trackClickOnSaveAs('slide');
                }
              : noop,
            isDisabled: !isSlideSavable,
            description: getPresentationSlideDisabledDescription({
              isViewConformant,
              isConvertedFromWorkspaceMode,
              i18nLocale,
            }),
          } satisfies DropdownOption,
        ]
      : []),
    ...(canSaveViewpoint && hasFeature(Features.BOOKMARKS)
      ? [
          {
            type: DropdownOptionType.OPTION,
            iconName: IconName.BOOKMARK,
            label: t(
              i18nLocale,
              'appContainer:saveDropdown.bookmark',
              'Bookmark'
            ),
            onClick: () =>
              onSaveAsBookmarkClick(isSaveAsBookmarkSidePanelOpened),
          } satisfies DropdownOption,
        ]
      : []),

    ...(addToDashboard
      ? [
          {
            type: DropdownOptionType.OPTION,
            iconName: IconName.DASHBOARD,
            label: t(
              i18nLocale,
              'appContainer:saveDropdown.dashboardWidget',
              'Dashboard widget'
            ),
            isDisabled:
              saveAsViewpointButtonState.isDisabled ||
              addToDashboard.isDisabled,
            description:
              addToDashboard.isDisabled &&
              !saveAsViewpointButtonState.isDisabled
                ? t(
                    i18nLocale,
                    'appContainer:saveDropdown.dashboardDisabledNoData',
                    'You cannot add this view to a dashboard because it has no data.'
                  )
                : saveAsViewpointButtonState.addToDashboardDisabledDescription,
            onClick:
              saveAsViewpointButtonState.isDisabled || addToDashboard.isDisabled
                ? noop
                : () => {
                    trackClickOnSaveAs('dashboard');
                    return addToDashboard.onClick();
                  },
          } satisfies DropdownOption,
        ]
      : []),

    ...(hasLucidchartFeature &&
    (mainViewId === ViewIds.BLOCK_DIAGRAM ||
      mainViewId === ViewIds.MODERNIZED_BLOCK_DIAGRAM)
      ? [
          {
            type: DropdownOptionType.OPTION,
            iconName: IconName.LUCIDCHART,
            label: t(
              i18nLocale,
              'appContainer:saveDropdown.lucidchartDocument',
              'Lucidchart document'
            ),
            onClick: () => exportToLucidchart(isLegendActive),
          } satisfies DropdownOption,
        ]
      : []),
    ...(canSaveViewpoint || canAddPresentationSlide
      ? [
          {
            type: DropdownOptionType.HEADER,
            label: t(
              i18nLocale,
              'appContainer:saveDropdown.downloadHeader',
              'Download'
            ),
          } satisfies DropdownHeader,
        ]
      : []),
    {
      type: DropdownOptionType.OPTION,
      iconName: IconName.EXPORT,
      label: t(i18nLocale, 'appContainer:saveDropdown.png', 'PNG'),
      onClick: () => {
        trackClickOnSaveAs('png');
        return exportToPng();
      },
      isDisabled: exportToPng === asyncNoop,
      testId: 'export-png',
    } satisfies DropdownOption,
    ...(exportToSvg && exportToSvg !== asyncNoop
      ? [
          {
            type: DropdownOptionType.OPTION,
            iconName: IconName.EXPORT,
            label: t(i18nLocale, 'appContainer:saveDropdown.svg', 'SVG'),
            onClick: () => {
              trackClickOnSaveAs('svg');
              return exportToSvg();
            },
            testId: 'export-svg',
          } satisfies DropdownOption,
        ]
      : []),
    ...(hasPptxExportFeature &&
    isViewpointMode &&
    !NOT_EXPORTABLE_VIEWS.has(mainViewId)
      ? [
          {
            type: DropdownOptionType.OPTION,
            iconName: IconName.EXPORT,
            label: t(i18nLocale, 'appContainer:saveDropdown.pptx', 'PPTX'),
            onClick: () => dispatchAction(viewPptxExportRequested()),
            isDisabled: exportToPng === asyncNoop,
            testId: 'export-pptx',
          } satisfies DropdownOption,
        ]
      : []),
    ...(mainViewId === ViewIds.MODERNIZED_BLOCK_DIAGRAM
      ? [
          {
            type: DropdownOptionType.OPTION,
            iconName: IconName.EXPORT,
            label: t(
              i18nLocale,
              'appContainer:saveDropdown.plantUml',
              'PlantUML'
            ),
            onClick: () => {
              trackClickOnSaveAs('plantuml');
              dispatchAction(exportToPlantUml({ isViewpointMode }));
            },
            testId: 'export-plantuml',
          } satisfies DropdownOption,
        ]
      : []),
  ];
};
